import './Candidate_Interview_Timings_Container.css';
import { useState, useEffect, useMemo, JSX } from 'react';
import { Pairing, InterviewPairingsResponse } from '../Requests/interviewPairings';
import type { SelectedSlotWithContext } from '../Pages/Candidate_Page1';
import Icons_Repository from '../Icons/Icons_Repository';

interface TimingsContainerProps {
  pairingsData: InterviewPairingsResponse;
  selectedDates: Date[];
  onSlotClick: (slot: SelectedSlotWithContext) => void;
  rescheduleClicked: boolean;
  setIsRescheduledClicked: (val: boolean) => void;
  setSelectedPage: (page: string) => void;
}

function Candidate_Interview_Timings_Container({
  pairingsData,
  selectedDates,
  onSlotClick,
  rescheduleClicked,
  setIsRescheduledClicked,
  setSelectedPage
}: TimingsContainerProps) {


  console.log(pairingsData);
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [currentPage, setCurrentPage] = useState<number>(0);
  const maxcardsperpage = 5;
  const getAbbreviation = () => {
    try {
      // Get abbreviation via Intl first
      const abbr = new Date().toLocaleTimeString('en-us', {
        timeZone: browserTimeZone,
        timeZoneName: 'short',
      }).split(' ').pop();

      // If it's already a short abbreviation (3–4 uppercase letters), use it
      if (abbr && /^[A-Z]{2,4}$/.test(abbr)) {
        return abbr;
      }

      // Otherwise, map common ones manually
      const knownZones: Record<string, string> = {
        'Asia/Kolkata': 'IST',
        'Asia/Calcutta': 'IST',
        'America/New_York': 'EST',
        'America/Los_Angeles': 'PST',
        'America/Chicago': 'CST',
        'America/Denver': 'MST',
        'Europe/London': 'GMT',
        'Europe/Paris': 'CET',
        'Europe/Berlin': 'CET',
        'Asia/Dubai': 'GST',
        'Asia/Tokyo': 'JST',
        'Australia/Sydney': 'AEDT',
        'Australia/Melbourne': 'AEDT',
      };

      return knownZones[browserTimeZone] || abbr || browserTimeZone;
    } catch {
      return 'UTC';
    }
  };

  const toMDY = (ymd: string) => {
    if (!ymd) return ymd;
    const [y, m, d] = ymd.split('-');
    return `${m}-${d}-${y}`;
  };

  const toLongDate = (ymd: string): JSX.Element | string => {
    if (!ymd) return ymd;

    const [year, month, day] = ymd.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const getOrdinalSuffix = (n: number): JSX.Element => {
      let suffix = 'th';
      if (n < 11 || n > 13) {
        switch (n % 10) {
          case 1: suffix = 'st'; break;
          case 2: suffix = 'nd'; break;
          case 3: suffix = 'rd'; break;
        }
      }
      return <>{n}</>;
    };

    const weekday = weekdays[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayWithSuffix = getOrdinalSuffix(date.getDate());

    return (
      <>
        {weekday}, {monthName} {dayWithSuffix}, {year}
      </>
    );
  };

  // Filter pairings for selected dates
  const filteredSlots = useMemo(() => {
    const selectedYMDs = selectedDates.map(date => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });

    return (pairingsData?.pairings ?? [])
      .filter((pairing) => pairing.slot_available) // only available slots
      .filter((pairing) => {
        const d = new Date(pairing.start_time);
        const ymd = d.toLocaleDateString('en-CA', { timeZone: browserTimeZone });
        // const ymd = d.toDateString();
        return selectedYMDs.includes(ymd);
      });
  }, [pairingsData, selectedDates]);




  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDates]);

  const totalPages = Math.ceil(filteredSlots.length / maxcardsperpage);
  const startIndex = currentPage * maxcardsperpage;
  const endIndex = startIndex + maxcardsperpage;
  const currentpageslots = filteredSlots.slice(startIndex, endIndex);

  const groupStart = Math.floor(currentPage / 3) * 3;
  const dots: any[] = [];

  for (let i = groupStart; i < groupStart + 3 && i < totalPages; i++) {
    dots.push(
      <button
        key={i}
        onClick={() => setCurrentPage(i)}
        className={`dot-btn ${i === currentPage ? 'currentpage' : 'otherpage'}`}
      />
    );
  }

  return (
    <div className="main-interview_timings-container">
      <div className='main-interview-timings-header'>
        <div className='main-interview-timings-header-text'>Interview timings</div>
        {rescheduleClicked && (
          <div className='see-current-slot-link'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                setIsRescheduledClicked(false);
                setSelectedPage('slotdetails');
              }}
              className='see-current-slot-text'
            >
              Exit Selection
            </a>
          </div>
        )}
      </div>
      <div className='main-interview-timings-body'>
        <div className='main-interview-timings-body-card-container'>
          {filteredSlots.length === 0 && (
            <div className="empty-state">
              No interview slots available for the selected date(s).
            </div>
          )}

          {currentpageslots.map((pairing: Pairing, idx: number) => {
            const dStart = new Date(pairing.start_time);
            const dEnd = new Date(pairing.end_time);

            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const dateYMD = dStart.toLocaleDateString('en-CA', { timeZone: browserTimeZone });
            const dayName = dStart.toLocaleDateString('en-GB', { timeZone: browserTimeZone, weekday: 'long' });
            const startStr = dStart.toLocaleTimeString('en-US', {
              timeZone: browserTimeZone, hour: 'numeric', minute: '2-digit', hour12: true
            });
            const endStr = dEnd.toLocaleTimeString('en-US', {
              timeZone: browserTimeZone, hour: 'numeric', minute: '2-digit', hour12: true
            });


            const timeRange = `${startStr} – ${endStr}`;
            const status = pairing.slot_available ? 'Select' : 'Booked';

            const slotForClick: SelectedSlotWithContext = {
              requisition_id: pairingsData.requisition_id,
              job_title: pairingsData.job_title,
              pairing: pairing,
            };


            return (
              <div
                // key={pairing.interview_pairing_id || idx}
                className="slot-timing-card"
                onClick={() => onSlotClick(slotForClick)}
              >
                <div className='slot-timing-card-details'>
                  <div className='slot-timing-card-details-day-date'>
                    <span>{toLongDate(dateYMD)}</span>
                  </div>
                  <div className='slot-timing-card-details-timing'>
                    {timeRange} {getAbbreviation()}
                  </div>
                </div>
                <div className='slot-timing-card-status'>
                  <div className={`slot-timing-card-status-text ${status === "Select" ? "available" : "booked"}`}>
                    {status}
                  </div>
                </div>
              </div>
            );

          })}
        </div>

        {filteredSlots.length > maxcardsperpage && (
          <div className='main-interview-timings-body-footer-navigation'>
            <div className='navigation-buttons'>
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 0))}
                disabled={currentPage === 0}
                className='prev-next-button'
                aria-label='Previous page'
              >
                <Icons_Repository iconName='chevron_left' />
              </button>

              {totalPages > 1 && <div className="page-dots">{dots}</div>}

              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages - 1))}
                disabled={currentPage === totalPages - 1}
                className='prev-next-button'
                aria-label='Next page'
              >
                <Icons_Repository iconName='chevron_right' />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Candidate_Interview_Timings_Container;